import { useState } from 'react';
import {
  Clock, ArrowRight, Tag, TrendingUp, Shield,
  Code2, AlertTriangle, BookOpen, Search,
} from 'lucide-react';
import PageLayout from '../components/PageLayout';

const CATEGORIES = ['All', 'Research', 'Tutorials', 'Security', 'Product', 'Industry'];

const POSTS = [
  {
    id: 1,
    title: 'Inside the ZenithCipher Risk Engine v2: How We Score Wallet Behavior',
    excerpt:
      'A deep-dive into the multi-factor scoring system behind our risk engine — transaction volume thresholds, bot-pattern detection, token farming signals, and how we minimize false positives.',
    category: 'Research',
    date: 'May 1, 2026',
    readTime: '12 min read',
    author: { name: 'Marcus Tran', initials: 'MT', gradient: 'from-[#f59e0b] to-[#f43f5e]' },
    icon: TrendingUp,
    accent: '#00E5FF',
    featured: true,
    tags: ['Risk Engine', 'Blockchain Analysis', 'Fraud Detection'],
  },
  {
    id: 2,
    title: 'How to Detect Mixer-Linked Wallets on Ethereum',
    excerpt:
      'Tornado Cash, Railgun, and other privacy tools leave detectable on-chain fingerprints. Learn the heuristics we use to identify mixer-linked addresses.',
    category: 'Research',
    date: 'Apr 22, 2026',
    readTime: '9 min read',
    author: { name: 'Sara Reyes', initials: 'SR', gradient: 'from-[#7C3AED] to-[#f43f5e]' },
    icon: Shield,
    accent: '#7C3AED',
    tags: ['Privacy', 'Mixers', 'AML'],
  },
  {
    id: 3,
    title: 'Integrating ZenithCipher into Your Node.js dApp in Under 10 Minutes',
    excerpt:
      'A practical tutorial for integrating our REST API into a Node.js backend — authentication, error handling, rate limit management, and caching scan results.',
    category: 'Tutorials',
    date: 'Apr 15, 2026',
    readTime: '7 min read',
    author: { name: 'James Okafor', initials: 'JO', gradient: 'from-[#8b5cf6] to-[#7C3AED]' },
    icon: Code2,
    accent: '#f59e0b',
    tags: ['Node.js', 'API', 'Integration'],
  },
  {
    id: 4,
    title: 'The Anatomy of a Rug Pull: On-Chain Signals That Warn You First',
    excerpt:
      'We analyzed 200 rug pulls from the past 18 months. Here are the wallet behavior patterns that consistently appeared 48–72 hours before the exit scam.',
    category: 'Security',
    date: 'Apr 8, 2026',
    readTime: '15 min read',
    author: { name: 'Alex Kim', initials: 'AK', gradient: 'from-[#00E5FF] to-[#7C3AED]' },
    icon: AlertTriangle,
    accent: '#f43f5e',
    tags: ['Rug Pulls', 'DeFi Security', 'Research'],
  },
  {
    id: 5,
    title: 'Airdrop Farming Wallets: Identifying the Sybil Attack Signatures',
    excerpt:
      'Sybil attackers use thousands of coordinated wallets to game airdrop distributions. This post covers the clustering techniques we use to identify them at scale.',
    category: 'Research',
    date: 'Mar 28, 2026',
    readTime: '11 min read',
    author: { name: 'Marcus Tran', initials: 'MT', gradient: 'from-[#f59e0b] to-[#f43f5e]' },
    icon: TrendingUp,
    accent: '#00E5FF',
    tags: ['Sybil', 'Airdrops', 'Clustering'],
  },
  {
    id: 6,
    title: 'Announcing ZenithCipher Enterprise: Custom Integrations & SLA',
    excerpt:
      "Today we're launching our Enterprise plan — on-premise deployment options, custom risk rule configuration, dedicated support, and 99.99% SLA for compliance teams.",
    category: 'Product',
    date: 'Mar 15, 2026',
    readTime: '4 min read',
    author: { name: 'Lena Wu', initials: 'LW', gradient: 'from-[#00E5FF] to-[#06b6d4]' },
    icon: Shield,
    accent: '#7C3AED',
    tags: ['Product', 'Enterprise', 'Announcement'],
  },
  {
    id: 7,
    title: 'Web3 AML in 2026: What Every Compliance Team Must Know',
    excerpt:
      'Regulatory pressure on crypto is intensifying globally. Here\'s a comprehensive overview of current AML requirements, FATF guidance, and how on-chain analytics helps you stay compliant.',
    category: 'Industry',
    date: 'Mar 3, 2026',
    readTime: '13 min read',
    author: { name: 'Priya Mehta', initials: 'PM', gradient: 'from-[#10b981] to-[#059669]' },
    icon: BookOpen,
    accent: '#10b981',
    tags: ['AML', 'Compliance', 'Regulation'],
  },
  {
    id: 8,
    title: 'Building a Wallet Screening Webhook Pipeline with ZenithCipher',
    excerpt:
      'Use our API + webhooks to build a real-time wallet screening pipeline for your exchange or DeFi platform. Covers queue patterns, retry logic, and alert routing.',
    category: 'Tutorials',
    date: 'Feb 18, 2026',
    readTime: '8 min read',
    author: { name: 'James Okafor', initials: 'JO', gradient: 'from-[#8b5cf6] to-[#7C3AED]' },
    icon: Code2,
    accent: '#f59e0b',
    tags: ['Webhooks', 'Pipeline', 'Tutorials'],
  },
];

function PostCard({ post, large = false }) {
  const { title, excerpt, category, date, readTime, author, icon: Icon, accent, tags } = post;
  return (
    <article className={`glass-card border border-white/[0.06] overflow-hidden group hover:-translate-y-1 transition-all duration-300 flex flex-col ${large ? '' : ''}`}>
      {/* Color band */}
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />

      <div className="p-6 flex-1 flex flex-col">
        {/* Meta top */}
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-[10px] font-bold px-2.5 py-1 rounded-full border"
            style={{ background: `${accent}12`, color: accent, borderColor: `${accent}25` }}
          >
            {category}
          </span>
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <Clock size={10} />
            {readTime}
          </div>
        </div>

        {/* Icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
          style={{ background: `${accent}12`, border: `1px solid ${accent}20` }}
        >
          <Icon size={18} style={{ color: accent }} />
        </div>

        <h2 className={`font-bold text-white mb-3 leading-snug group-hover:text-[#00E5FF] transition-colors ${large ? 'text-xl' : 'text-base'}`}>
          {title}
        </h2>
        <p className="text-slate-500 text-sm leading-relaxed flex-1 mb-5">{excerpt}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {tags.map(t => (
            <span key={t} className="flex items-center gap-1 text-[10px] text-slate-600 bg-white/[0.04] border border-white/[0.07] px-2 py-0.5 rounded-full">
              <Tag size={8} /> {t}
            </span>
          ))}
        </div>

        {/* Author + date */}
        <div className="flex items-center justify-between pt-4 border-t border-white/[0.05]">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${author.gradient} flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0`}>
              {author.initials}
            </div>
            <div>
              <div className="text-xs font-medium text-slate-300">{author.name}</div>
              <div className="text-[10px] text-slate-600">{date}</div>
            </div>
          </div>
          <button className="flex items-center gap-1 text-xs font-medium group-hover:gap-2 transition-all" style={{ color: accent }}>
            Read <ArrowRight size={11} />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = POSTS.filter(p => {
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const [featured, ...rest] = filtered;

  return (
    <PageLayout
      badge="Blog"
      badgeColor="purple"
      title={<>Security insights, research<br /><span className="text-[#7C3AED]">&amp; Web3 intelligence</span></>}
      subtitle="Deep dives into blockchain security, fraud detection research, product updates, and practical tutorials from the ZenithCipher team."
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">

        {/* Filters + Search */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-10">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={[
                  'px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all',
                  activeCategory === cat
                    ? 'bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/25'
                    : 'text-slate-500 border-white/[0.08] hover:text-slate-200 hover:border-white/20',
                ].join(' ')}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.07] rounded-lg px-3 py-2 w-full sm:w-56">
            <Search size={13} className="text-slate-600" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search posts…"
              className="bg-transparent text-xs text-slate-300 placeholder-slate-600 outline-none flex-1"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-600">
            <BookOpen size={36} className="mx-auto mb-3" />
            <p className="text-sm">No posts match your filter.</p>
          </div>
        ) : (
          <>
            {/* Featured post */}
            {featured && activeCategory === 'All' && !search && (
              <div className="mb-8">
                <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3">Featured Post</div>
                <PostCard post={featured} large />
              </div>
            )}

            {/* Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {(activeCategory === 'All' && !search ? rest : filtered).map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          </>
        )}

        {/* Newsletter */}
        <div className="mt-20 glass-card border border-[#7C3AED]/20 p-10 rounded-2xl text-center">
          <BookOpen size={26} className="text-[#7C3AED] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Stay ahead of Web3 threats</h3>
          <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
            Get our latest research, tutorials, and security advisories delivered to your inbox. No spam — ever.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
            <input
              type="email"
              placeholder="your@email.com"
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-300 placeholder-slate-600 outline-none focus:border-[#7C3AED]/40 transition-colors"
            />
            <button className="bg-[#7C3AED] hover:bg-[#6d35d4] text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-all flex-shrink-0">
              Subscribe
            </button>
          </div>
          <p className="text-[10px] text-slate-600 mt-3">Sent roughly 2x per month. Unsubscribe anytime.</p>
        </div>

      </div>
    </PageLayout>
  );
}
